import { useMemo, useState } from 'react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { formatTime } from '@shared/format.js';

const RANGES = [
  { key: '7d', label: '近7天' },
  { key: '30d', label: '近30天' },
  { key: '90d', label: '近3个月' },
  { key: 'all', label: '全部' },
];

const SERIES = [
  { key: 'avg', label: '日均', color: '#22D3EE' },
  { key: 'best', label: '当日最佳', color: '#34D399' },
  { key: 'ao5', label: '滚动 Ao5', color: '#818CF8' },
  { key: 'ao12', label: '滚动 Ao12', color: '#FBBF24' },
];

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-ink-900 border border-slate-700 rounded-xl px-3 py-2 text-xs shadow-xl">
      <div className="text-slate-400 mb-1">{label}</div>
      {payload
        .filter((p) => p.value !== null && p.value !== undefined)
        .map((p) => (
          <div key={p.dataKey} className="flex items-center gap-2 py-0.5">
            <span className="w-2 h-2 rounded-full" style={{ background: p.color || p.stroke }} />
            <span className="text-slate-400">{SERIES.find((s) => s.key === p.dataKey)?.label || p.dataKey}</span>
            <span className="ml-auto font-mono text-slate-100">{formatTime(p.value)}</span>
          </div>
        ))}
    </div>
  );
}

export default function TrendChart({ trend, range, onRangeChange }) {
  const [hidden, setHidden] = useState({});
  const data = useMemo(() => {
    if (!trend) return [];
    const map = new Map();
    for (const d of trend.daily || []) {
      map.set(d.date, { date: d.date, avg: d.avg, best: d.best });
    }
    for (const p of trend.rolling?.ao5 || []) {
      const cur = map.get(p.date) || { date: p.date };
      cur.ao5 = p.value;
      map.set(p.date, cur);
    }
    for (const p of trend.rolling?.ao12 || []) {
      const cur = map.get(p.date) || { date: p.date };
      cur.ao12 = p.value;
      map.set(p.date, cur);
    }
    return [...map.values()].sort((a, b) => (a.date < b.date ? -1 : 1));
  }, [trend]);

  const toggle = (key) => setHidden((h) => ({ ...h, [key]: !h[key] }));

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex gap-1.5">
          {RANGES.map((r) => (
            <button
              key={r.key}
              onClick={() => onRangeChange(r.key)}
              className={`tab ${range === r.key ? 'tab-active' : 'tab-idle'}`}
            >
              {r.label}
            </button>
          ))}
        </div>
        <div className="flex gap-3 flex-wrap">
          {SERIES.map((s) => (
            <button key={s.key} onClick={() => toggle(s.key)} className="flex items-center gap-1.5 text-xs">
              <span
                className="w-2.5 h-2.5 rounded-sm"
                style={{ background: s.color, opacity: hidden[s.key] ? 0.25 : 1 }}
              />
              <span className={hidden[s.key] ? 'text-slate-600 line-through' : 'text-slate-400'}>{s.label}</span>
            </button>
          ))}
        </div>
      </div>

      {data.length === 0 ? (
        <div className="text-center text-sm text-slate-500 py-16">
          该时间段暂无成绩数据
        </div>
      ) : (
        <div className="h-72 sm:h-80 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: -8 }}>
              <CartesianGrid stroke="#1E2A47" strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tick={{ fill: '#64748B', fontSize: 11 }}
                tickFormatter={(v) => v.slice(5)}
                minTickGap={28}
                axisLine={{ stroke: '#334155' }}
                tickLine={false}
              />
              <YAxis
                reversed
                tick={{ fill: '#64748B', fontSize: 11 }}
                tickFormatter={(v) => formatTime(v)}
                domain={['auto', 'auto']}
                axisLine={{ stroke: '#334155' }}
                tickLine={false}
                width={64}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 12, color: '#94A3B8' }} />
              {SERIES.map((s) =>
                hidden[s.key] ? null : (
                  <Line
                    key={s.key}
                    type="monotone"
                    dataKey={s.key}
                    stroke={s.color}
                    strokeWidth={s.key === 'avg' ? 2.5 : 1.8}
                    dot={false}
                    connectNulls
                    activeDot={{ r: 4 }}
                  />
                )
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
      <p className="text-[11px] text-slate-600 mt-2">
        * 纵轴已反转：曲线越高代表越快；Ao5/Ao12 为滚动平均（去掉最好最差）
      </p>
    </div>
  );
}
